"use client";

import Image from "next/image";
import { cn } from "@/lib/utils";
import {
  BROWSE_CATEGORIES,
  CATEGORY_PHOTO_IDS,
  type BrowseCategory,
} from "@/components/feed/browse-categories";

/**
 * BrowseSidebar — the desktop feed's left rail (mockup: Kraahl Desktop Home,
 * 236px). A "BROWSE" overline + category rows. Presentational: it reports the
 * selected category id (or null for "All items") via `onSelectAction`; the feed
 * shell owns the filtering. `activeId` highlights the current selection.
 *
 * The category taxonomy itself lives in the server-safe `browse-categories`
 * module (re-exported here for existing client callers).
 */

export { BROWSE_CATEGORIES, type BrowseCategory };

export function BrowseSidebar({
  activeId = "all",
  onSelectAction,
  className,
}: {
  activeId?: string;
  onSelectAction: (id: string) => void;
  className?: string;
}) {
  return (
    <aside
      className={cn(
        "hide-scrollbar h-full w-[236px] shrink-0 overflow-y-auto border-r border-border/60 px-3.5 pb-6 pt-5",
        className,
      )}
    >
      <div className="px-3 pb-2.5 pt-0.5 text-[11px] font-semibold uppercase tracking-[0.13em] text-muted-foreground">
        Browse
      </div>
      <nav className="flex flex-col gap-0.5">
        {BROWSE_CATEGORIES.map(({ id, label, icon: Icon }) => {
          const active = id === activeId;
          return (
            <button
              key={id}
              type="button"
              onClick={() => onSelectAction(id)}
              aria-current={active ? "page" : undefined}
              className={cn(
                "flex items-center gap-3 rounded-full px-3 py-2 text-sm font-medium transition-colors",
                active
                  ? "bg-accent text-accent-foreground"
                  : "text-foreground/80 hover:bg-accent hover:text-accent-foreground",
              )}
            >
              {CATEGORY_PHOTO_IDS.has(id) ? (
                /* Real product cutout instead of a generic glyph — same webp
                   the landing tiles use (one browser-cache entry for both).
                   `unoptimized`: serve-ready static asset, and the optimizer's
                   AVIF encoder hangs on some variants of these exact files
                   (see shop-by-category.tsx). Literal template src per the
                   fitness-next-image-params local-asset convention. */
                <Image
                  src={`/categories/${id}.webp`}
                  alt=""
                  aria-hidden
                  width={24}
                  height={24}
                  unoptimized
                  className="size-6 shrink-0 object-contain"
                />
              ) : (
                <Icon className="size-[19px] shrink-0" strokeWidth={2} />
              )}
              {label}
            </button>
          );
        })}
      </nav>
    </aside>
  );
}
